import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { ArrowLeft, Award, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const CreditScorePage = () => {
  const navigate = useNavigate();
  const [pan, setPan] = useState('');
  const [fullName, setFullName] = useState('');
  const [scoreData, setScoreData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleCheck = async () => {
    if (!pan || pan.length !== 10) {
      toast.error('Please enter valid 10-character PAN');
      return;
    }
    if (!fullName) {
      toast.error('Please enter your full name');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/credit-score`, {
        pan: pan.toUpperCase(),
        full_name: fullName
      });
      setScoreData(response.data);
      toast.success('Credit score retrieved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get credit score');
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 800) return '#FF6B35';
    if (score >= 750) return '#16A34A';
    if (score >= 700) return '#FFB627';
    if (score >= 650) return '#F59E0B';
    return '#EF4444';
  };

  const getScorePercentage = (score) => {
    return ((score - 600) / (850 - 600)) * 100;
  };

  return (
    <div style={{ backgroundColor: '#F8FAFC', minHeight: '100vh' }}>
      <Navbar />

      <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '4rem 2rem' }}>
        <button 
          onClick={() => navigate('/')}
          style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'none', border: 'none', color: '#FF6B35', cursor: 'pointer', marginBottom: '2rem', fontWeight: '600' }}
          data-testid="back-to-home-button"
        >
          <ArrowLeft size={20} /> Back to Home
        </button>

        <div style={{ marginBottom: '3rem' }}>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', marginBottom: '0.5rem', color: '#0F172A' }}>
            Free Credit Score
          </h1>
          <p style={{ fontSize: '1.125rem', color: '#475569' }}>
            Check your credit score for free and get personalized tips to improve it
          </p>
        </div>

        {!scoreData ? (
          <div className="calculator-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '2rem' }}>
              <Award size={32} style={{ color: '#FF6B35' }} />
              <h2 style={{ fontSize: '1.5rem', color: '#0F172A' }}>Check Your Credit Score</h2>
            </div>

            <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2rem' }}>
              <div>
                <label className="label-text">PAN Number</label>
                <input
                  type="text"
                  className="input-field"
                  value={pan}
                  onChange={(e) => setPan(e.target.value.toUpperCase().slice(0, 10))}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  data-testid="credit-score-pan-input"
                />
              </div>

              <div>
                <label className="label-text">Full Name (as per PAN)</label>
                <input
                  type="text"
                  className="input-field"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter your full name"
                  data-testid="credit-score-name-input"
                />
              </div>
            </div>

            <button
              onClick={handleCheck}
              className="primary-button"
              disabled={loading}
              style={{ width: '100%', justifyContent: 'center' }}
              data-testid="check-credit-score-button"
            >
              {loading ? 'Checking...' : 'Get My Credit Score (Free)'}
            </button>

            <div style={{ marginTop: '2rem', padding: '1.5rem', backgroundColor: '#FFF7ED', borderRadius: '1rem' }}>
              <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: '1.6' }}>
                <strong style={{ color: '#0F172A' }}>100% Free & Secure:</strong> Checking your credit score here has no impact on your score. Your data is encrypted and never shared.
              </p>
            </div>
          </div>
        ) : (
          <div className="fade-in">
            {/* Score Display */}
            <div className="calculator-section" style={{ marginBottom: '2rem', textAlign: 'center' }} data-testid="credit-score-result">
              <h2 style={{ fontSize: '1.25rem', color: '#475569', marginBottom: '1rem' }}>Your Credit Score</h2>
              <div style={{ fontSize: 'clamp(4rem, 8vw, 6rem)', fontWeight: '800', color: getScoreColor(scoreData.credit_score), fontFamily: 'Outfit', lineHeight: 1 }}>
                {scoreData.credit_score}
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: '700', color: getScoreColor(scoreData.credit_score), marginTop: '0.5rem' }}>
                {scoreData.rating}
              </div>
              <div style={{ fontSize: '0.875rem', color: '#475569', marginTop: '0.5rem' }}>
                Range: 600 - 850 (Generated for demo)
              </div>

              {/* Progress Bar */}
              <div style={{ marginTop: '2rem', width: '100%', maxWidth: '500px', margin: '2rem auto 0' }}>
                <div style={{ width: '100%', height: '20px', backgroundColor: '#E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                  <div 
                    style={{ 
                      width: `${getScorePercentage(scoreData.credit_score)}%`, 
                      height: '100%', 
                      backgroundColor: getScoreColor(scoreData.credit_score),
                      transition: 'width 1s ease-out'
                    }}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.75rem', color: '#475569' }}>
                  <span>600</span>
                  <span>650</span>
                  <span>700</span>
                  <span>750</span>
                  <span>800</span>
                  <span>850</span>
                </div>
              </div>

              <button
                onClick={() => setScoreData(null)}
                className="outline-button"
                style={{ marginTop: '2rem' }}
                data-testid="check-another-score-button"
              >
                Check Another Score
              </button>
            </div>

            {/* Factors */}
            <div className="calculator-section" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <TrendingUp size={24} style={{ color: '#FF6B35' }} />
                Factors Affecting Your Score
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
                {Object.entries(scoreData.factors).map(([factor, status]) => (
                  <div key={factor} style={{ padding: '1rem', backgroundColor: '#FFF7ED', borderRadius: '0.75rem' }}>
                    <div style={{ fontSize: '0.875rem', color: '#475569', marginBottom: '0.5rem' }}>{factor}</div>
                    <div style={{ fontWeight: '700', color: ['Good', 'Optimal', 'Mature', 'Diverse', 'Low'].includes(status) ? '#FF6B35' : '#004D7A' }}>
                      {status}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Tips */}
            <div className="calculator-section">
              <h3 style={{ fontSize: '1.25rem', color: '#0F172A', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <AlertTriangle size={24} style={{ color: '#004D7A' }} />
                Tips to Improve Your Score
              </h3>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {scoreData.tips.map((tip, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '1rem', backgroundColor: '#F8FAFC', borderRadius: '0.75rem', border: '1px solid #E2E8F0' }}>
                    <CheckCircle size={20} style={{ color: '#FF6B35', flexShrink: 0, marginTop: '2px' }} />
                    <p style={{ color: '#0F172A', lineHeight: '1.6' }}>{tip}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditScorePage;
